// Web Push helpers: service worker registration + VAPID subscription.
import api from "@/lib/api";

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission() {
  return typeof window !== "undefined" && "Notification" in window
    ? Notification.permission
    : "unsupported";
}

export function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

// iOS Safari only exposes PushManager when launched from the Home Screen.
export function needsHomeScreenInstall() {
  return isIOS() && !isStandalone();
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch (e) {
    return null;
  }
}

// Must be triggered from a user gesture. Returns { status }.
export async function enablePush() {
  if (!pushSupported()) {
    return { status: needsHomeScreenInstall() ? "needs-install" : "unsupported" };
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { status: permission };

  const reg =
    (await navigator.serviceWorker.getRegistration()) || (await registerServiceWorker());
  if (!reg) return { status: "error" };
  await navigator.serviceWorker.ready;

  let data;
  try {
    ({ data } = await api.get("/push/vapid-public-key"));
  } catch (e) {
    return { status: "error" };
  }
  if (!data.enabled || !data.public_key) return { status: "server-disabled" };

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  await api.post("/push/subscribe", { subscription: sub.toJSON() });
  return { status: "granted" };
}

export async function disablePush() {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try {
      await api.post("/push/unsubscribe", { subscription: sub.toJSON() });
    } catch (e) {}
    await sub.unsubscribe();
  }
}
